import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MessagesSquare } from 'lucide-react';

import { listAiChatThreads, type UserAiChatThread } from '../services/userAiChatService';

function contextPreview(thread: UserAiChatThread): string {
  const context = thread.context || {};
  const segments = Array.isArray((context as { selectedSegments?: unknown }).selectedSegments)
    ? (context as { selectedSegments: Array<{ text?: string }> }).selectedSegments
    : [];
  const texts = segments.map((item) => String(item.text || '').trim()).filter(Boolean);
  if (texts.length) return texts.join(' · ').slice(0, 120);
  return '（无选段摘要）';
}

export function LearningAiChatsPage() {
  const [threads, setThreads] = useState<UserAiChatThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setThreads(await listAiChatThreads({ limit: 100 }));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <MessagesSquare size={18} className="text-primary" />
        <h1 className="text-lg font-semibold text-ink">AI 对话</h1>
      </div>
      <p className="text-sm text-muted">全部对话按更新时间排列；每条都带有可跳转的上下文。</p>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-primary" />
        </div>
      ) : null}
      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {!loading && !error && threads.length === 0 ? (
        <p className="rounded-xl border border-border bg-surface px-4 py-8 text-center text-sm text-muted">
          暂无对话。在品题画布或知识颗粒详情选段后即可发起。
        </p>
      ) : null}

      <ul className="space-y-2">
        {threads.map((thread) => (
          <li key={thread.id}>
            <Link
              to={`/learning/ai-chats/${thread.id}`}
              className="block rounded-xl border border-border bg-surface px-4 py-3 hover:bg-black/[0.02]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">
                    {thread.title || '未命名对话'}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs text-muted">{contextPreview(thread)}</p>
                </div>
                <time className="shrink-0 text-[11px] text-muted">
                  {new Date(thread.updatedAt).toLocaleString()}
                </time>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
